import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  IconButton,
  Badge,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  Menu,
  MenuItem,
  Fade,
  Paper,
  Tooltip
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  NotificationsNone as NotificationsNoneIcon,
  MarkunreadMailbox as UnreadIcon,
  MarkEmailRead as ReadIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Delete as DeleteIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { notificationsAPI } from '../services/api.js';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, formatDistanceToNow } from 'date-fns';

const NotificationsDashboard = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filterAnchor, setFilterAnchor] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('ALL');

  const userId = user?.userId || user?.attributes?.sub || user?.username;

  // Fetch user notifications - optimized for dashboard view
  const { data: notifications = [], isLoading, error } = useQuery(
    ['userNotifications', userId],
    () => userId ? notificationsAPI.getUserNotifications(userId) : Promise.resolve({ data: [] }),
    {
      select: (response) => response.data || [],
      enabled: !!userId && !!user, // Only fetch if user is properly logged in
      refetchInterval: 120000, // Refetch every 2 minutes instead of 30 seconds
      refetchIntervalInBackground: false, // Don't refetch when tab is not active
      staleTime: 60000, // Consider data fresh for 1 minute
      cacheTime: 300000, // Keep in cache for 5 minutes
      retry: 1, // Only retry once on failure
      retryDelay: 3000, // Wait 3 seconds before retry
    }
  );

  // Mark notification as read mutation
  const markAsReadMutation = useMutation(
    (notificationId) => notificationsAPI.markAsRead(notificationId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['userNotifications', userId]);
      },
    }
  );

  // Don't render dashboard if user is not logged in
  if (!user || !userId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6">Please log in to view notifications</Typography>
      </Box>
    );
  }

  // Priority configurations
  const priorityConfig = {
    LOW: { color: 'success', icon: <InfoIcon />, bgColor: '#e8f5e8' },
    MEDIUM: { color: 'warning', icon: <InfoIcon />, bgColor: '#fff8e1' },
    HIGH: { color: 'error', icon: <WarningIcon />, bgColor: '#ffebee' },
    URGENT: { color: 'error', icon: <ErrorIcon />, bgColor: '#ffcdd2' }
  };

  // Category configurations
  const categoryConfig = {
    MAINTENANCE: { label: 'Maintenance', emoji: '🔧' },
    UTILITY: { label: 'Utilities', emoji: '⚡' },
    FINANCIAL: { label: 'Financial', emoji: '💰' },
    SECURITY: { label: 'Security', emoji: '🔒' },
    COMMUNITY: { label: 'Community', emoji: '👥' },
    POLICY: { label: 'Policy', emoji: '📋' },
    FACILITY: { label: 'Facility', emoji: '🏢' },
    EMERGENCY: { label: 'Emergency', emoji: '🚨' },
    GENERAL: { label: 'General', emoji: '📢' }
  };

  // Filter options
  const filterOptions = [
    { value: 'ALL', label: 'All Notifications' },
    { value: 'UNREAD', label: 'Unread Only' },
    { value: 'HIGH_PRIORITY', label: 'High Priority' },
    { value: 'MAINTENANCE', label: 'Maintenance' },
    { value: 'FINANCIAL', label: 'Financial' },
    { value: 'COMMUNITY', label: 'Community' },
    { value: 'EMERGENCY', label: 'Emergency' }
  ];

  // Filter notifications based on selected filter
  const filteredNotifications = notifications.filter(notification => {
    switch (selectedFilter) {
      case 'UNREAD':
        return !notification.isRead;
      case 'HIGH_PRIORITY':
        return ['HIGH', 'URGENT'].includes(notification.priority);
      case 'MAINTENANCE':
      case 'FINANCIAL':
      case 'COMMUNITY':
      case 'EMERGENCY':
        return notification.category === selectedFilter;
      default:
        return true;
    }
  });

  // Count unread notifications
  const unreadCount = notifications.filter(n => !n.isRead).length;

  // Handle notification click
  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setDialogOpen(true);
    
    // Mark as read if unread
    if (!notification.isRead) {
      markAsReadMutation.mutate(notification.notificationId);
    }
  };

  // Format notification date
  const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else {
      return format(date, 'MMM dd, yyyy HH:mm');
    }
  };

  // Get notification avatar color based on category and read status
  const getAvatarProps = (notification) => {
    const config = categoryConfig[notification.category] || categoryConfig.GENERAL;
    const priority = priorityConfig[notification.priority] || priorityConfig.MEDIUM;
    
    return {
      sx: {
        bgcolor: notification.isRead ? 'grey.300' : priority.bgColor,
        color: notification.isRead ? 'grey.600' : priority.color + '.main'
      },
      children: config.emoji
    };
  };

  if (!userId) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" color="error">
            Please log in to view notifications
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">
            Failed to load notifications: {error.message}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge badgeContent={unreadCount} color="error">
            <NotificationsIcon fontSize="large" />
          </Badge>
          <Typography variant="h4">
            {t('notifications.title')}
          </Typography>
        </Box>

        {/* Filter Button */}
        <Button
          startIcon={<FilterIcon />}
          onClick={(e) => setFilterAnchor(e.currentTarget)}
          variant="outlined"
        >
          {filterOptions.find(f => f.value === selectedFilter)?.label}
        </Button>
      </Box>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterAnchor}
        open={Boolean(filterAnchor)}
        onClose={() => setFilterAnchor(null)}
        TransitionComponent={Fade}
      >
        {filterOptions.map((option) => (
          <MenuItem
            key={option.value}
            selected={selectedFilter === option.value}
            onClick={() => {
              setSelectedFilter(option.value);
              setFilterAnchor(null);
            }}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>

      {/* Summary Stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Paper sx={{ p: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={notifications.length} color="primary" showZero>
              <NotificationsIcon />
            </Badge>
            <Box>
              <Typography variant="h6">{notifications.length}</Typography>
              <Typography variant="body2" color="text.secondary">Total</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={unreadCount} color="error" showZero>
              <UnreadIcon />
            </Badge>
            <Box>
              <Typography variant="h6">{unreadCount}</Typography>
              <Typography variant="body2" color="text.secondary">Unread</Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Badge badgeContent={notifications.filter(n => ['HIGH', 'URGENT'].includes(n.priority)).length} color="warning" showZero>
              <WarningIcon />
            </Badge>
            <Box>
              <Typography variant="h6">
                {notifications.filter(n => ['HIGH', 'URGENT'].includes(n.priority)).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">High Priority</Typography>
            </Box>
          </Box>
        </Paper>
      </Box>

      {/* Notifications List */}
      <Card>
        <CardContent>
          {isLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Loading notifications...</Typography>
            </Box>
          ) : filteredNotifications.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotificationsNoneIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
              <Typography variant="h6" color="text.secondary">
                {selectedFilter === 'UNREAD' ? 'No unread notifications' : 'No notifications found'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedFilter === 'ALL' 
                  ? 'You\'re all caught up!' 
                  : 'Try changing the filter to see more notifications.'
                }
              </Typography>
            </Box>
          ) : (
            <List>
              {filteredNotifications.map((notification, index) => (
                <React.Fragment key={notification.notificationId}>
                  <ListItem
                    button
                    onClick={() => handleNotificationClick(notification)}
                    sx={{
                      bgcolor: notification.isRead ? 'transparent' : 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' },
                      borderRadius: 1,
                      mb: 1
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar {...getAvatarProps(notification)} />
                    </ListItemAvatar>
                    
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: notification.isRead ? 'normal' : 'bold',
                              flex: 1
                            }}
                          >
                            {notification.title}
                          </Typography>
                          <Chip
                            size="small"
                            label={notification.priority}
                            color={priorityConfig[notification.priority]?.color || 'default'}
                          />
                          {!notification.isRead && (
                            <Chip size="small" label="NEW" color="primary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              color: notification.isRead ? 'text.secondary' : 'text.primary',
                              mb: 0.5
                            }}
                          >
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip
                              size="small"
                              label={categoryConfig[notification.category]?.label || notification.category}
                              variant="outlined"
                            />
                            <Typography variant="caption" color="text.secondary">
                              {formatNotificationDate(notification.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      }
                    />

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {notification.isRead ? (
                        <Tooltip title="Read">
                          <ReadIcon color="action" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Unread">
                          <UnreadIcon color="primary" />
                        </Tooltip>
                      )}
                    </Box>
                  </ListItem>
                  
                  {index < filteredNotifications.length - 1 && <Divider variant="inset" />}
                </React.Fragment>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      {/* Notification Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            maxHeight: '80vh'
          }
        }}
      >
        {selectedNotification && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar {...getAvatarProps(selectedNotification)} />
                  <Box>
                    <Typography variant="h6">{selectedNotification.title}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Chip
                        size="small"
                        label={selectedNotification.priority}
                        color={priorityConfig[selectedNotification.priority]?.color || 'default'}
                      />
                      <Chip
                        size="small"
                        label={categoryConfig[selectedNotification.category]?.label || selectedNotification.category}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={() => setDialogOpen(false)}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            
            <DialogContent>
              <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
                {selectedNotification.message}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Sent on: {format(new Date(selectedNotification.createdAt), 'MMMM dd, yyyy HH:mm')}
                </Typography>
                
                {selectedNotification.isRead && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon color="success" fontSize="small" />
                    <Typography variant="body2" color="text.secondary">
                      Read
                    </Typography>
                  </Box>
                )}
              </Box>

              {selectedNotification.isBuildingWide && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  This is a building-wide notification sent to multiple residents.
                </Alert>
              )}
            </DialogContent>

            <DialogActions>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default NotificationsDashboard;